import React, { useState, useMemo, useCallback } from 'react';
import { Box, Text, Select, Alert, Button, Badge, Group, Anchor } from '@mantine/core';
import { IconX, IconExternalLink } from '@tabler/icons-react';
import { getPluralizedLabel } from '../../utils/pluralizationUtils';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { DynamicTableView } from '../common/DynamicTableView';
import { createSchemaBindingManagementTableAdapter } from '../common/tableAdapters';
import { EntityDetailDialog } from '../dialogs';
import { entityService } from '../../services/entityServiceSingleton';
import { debugLog } from '../../utils/debugLog';
import { LoadingState } from '../common/LoadingState';

interface SchemaBindingManagementProps {
    selectedEntityId?: string | null;
    deepLinkEntityType?: string | null;
    authResolved?: boolean;
}

const SchemaBindingManagement: React.FC<SchemaBindingManagementProps> = ({
    selectedEntityId,
    deepLinkEntityType,
    authResolved = false
}) => {
    debugLog('🔍 SchemaBindingManagement: Component rendered with authResolved:', authResolved);
    const [selectedEntityType, setSelectedEntityType] = useState<string>('');
    const [selectedEntity, setSelectedEntity] = useState<any>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState<boolean>(false);
    const { entityTypes, isLoading, error } = useEntityTypes();
    debugLog('🔍 SchemaBindingManagement: entityTypes.length:', entityTypes.length, 'loading:', isLoading, 'error:', error);

    // Handle entity type selection
    const handleEntityTypeChange = (value: string | null) => {
        const newEntityType = value || '';
        debugLog('🔍 SchemaBindingManagement: Entity type changed to:', newEntityType);
        setSelectedEntityType(newEntityType);
        // Reset selected entity when changing entity type
        setSelectedEntity(null);
        setDetailDialogOpen(false);
    };

    // Handle clearing selection
    const handleClearSelection = useCallback(() => {
        setSelectedEntityType('');
        setSelectedEntity(null);
        setDetailDialogOpen(false);
    }, []);

    // Handle entity selection from table
    const handleEntitySelect = useCallback(async (entity: any) => {
        debugLog('🔍 SchemaBindingManagement: Entity selected:', entity);
        debugLog('🔍 SchemaBindingManagement: Entity.Id:', entity?.Id);
        debugLog('🔍 SchemaBindingManagement: Entity.Entity:', entity?.Entity);
        debugLog('🔍 SchemaBindingManagement: Entity.Entity?.Id:', entity?.Entity?.Id);
        debugLog('🔍 SchemaBindingManagement: Entity.Entity?.DisplayName:', entity?.Entity?.DisplayName);
        debugLog('🔍 SchemaBindingManagement: Entity.rawData:', entity?.rawData);
        debugLog('🔍 SchemaBindingManagement: Entity.rawData?.Id:', entity?.rawData?.Id);

        // If entity has rawData that is an object, use that (it has the correct structure)
        // Otherwise use the entity as-is
        const entityToUse = (entity?.rawData && typeof entity.rawData === 'object' && !Array.isArray(entity.rawData))
            ? entity.rawData
            : entity;
        setSelectedEntity(entityToUse);
        setDetailDialogOpen(true);
    }, []);

    const schemaBindingTableAdapter = useMemo(
        () =>
            createSchemaBindingManagementTableAdapter({
                authResolved,
                entityService,
                getSelectedEntityType: () => selectedEntityType,
                pageSize: 25
            }),
        [authResolved, entityService, selectedEntityType]
    );

    // Entity type options for Select
    const entityTypeOptions = useMemo(() => {
        return [
            { value: '', label: 'Select an entity type...' },
            ...entityTypes
                .filter(type => {
                    const typeName = type.name.toLowerCase();
                    return typeName !== 'schemabinding' &&
                        typeName !== 'schemaentity' &&
                        typeName !== 'schemaattribute';
                })
                .map(type => ({
                    value: type.name.toLowerCase(),
                    label: getPluralizedLabel(type.name)
                }))
        ];
    }, [entityTypes]);

    // Show loading state while entity types are loading
    if (isLoading) {
        return (
            <LoadingState variant="centered" size="sm" minHeight={400} label="Loading available entity types..." />
        );
    }

    // Show error if entity types failed to load
    if (error) {
        return (
            <Alert color="red" mb="md">
                Failed to load available entity types: {error}
            </Alert>
        );
    }

    return (
        <Box>
            <Text component="h4" fw={600} size="xl" mb="xs">
                Schema Binding Management
            </Text>
            <Text size="sm" c="dimmed" mb="lg">
                View and manage schema bindings. Select an entity type to filter bindings for that specific type.
            </Text>

            {/* Entity Type Selection Dropdown */}
            <Box mb="lg" p="md" bg="var(--mantine-color-default)" style={{ borderRadius: 4, border: '1px solid var(--mantine-color-default-border)' }}>
                <Text fw={600} size="sm" mb="xs">
                    Filter by Entity Type
                </Text>
                <Select
                    label="Entity Type"
                    placeholder="Select an entity type..."
                    value={selectedEntityType || null}
                    onChange={handleEntityTypeChange}
                    data={entityTypeOptions}
                    maw={300}
                    mb="xs"
                />
                {selectedEntityType ? (
                    <Group align="center" gap="md" wrap="wrap">
                        <Badge
                            variant="outline"
                            color="blue"
                        >
                            Viewing bindings for: {getPluralizedLabel(selectedEntityType)}
                        </Badge>
                        <Anchor
                            href={`#admin/${selectedEntityType}`}
                            size="sm"
                            title={`Open ${getPluralizedLabel(selectedEntityType)} table in new tab (middle-click)`}
                        >
                            <Group gap="xs" align="center" component="span">
                                <IconExternalLink size={16} />
                                View {getPluralizedLabel(selectedEntityType)} table
                            </Group>
                        </Anchor>
                        <Button
                            size="sm"
                            leftSection={<IconX size={16} />}
                            onClick={handleClearSelection}
                            variant="subtle"
                            color="gray"
                        >
                            Back to All Bindings
                        </Button>
                    </Group>
                ) : (
                    <Text size="sm" c="dimmed">
                        Select an entity type above to filter bindings, or browse all schema bindings below.
                    </Text>
                )}
            </Box>

            {/* Show schema bindings table */}
            <Box mt="md">
                <DynamicTableView
                    key={`schemabinding-${selectedEntityType || 'all'}`}
                        entityType={schemaBindingTableAdapter.entityType}
                    selectedEntityId={selectedEntityId}
                    deepLinkEntityType={deepLinkEntityType}
                    authResolved={authResolved}
                    onEntitySelect={handleEntitySelect}
                        loadData={schemaBindingTableAdapter.loadData}
                        deleteEntityById={schemaBindingTableAdapter.deleteById}
                />
            </Box>

            {/* Detail Dialog */}
            {selectedEntity && (
                <EntityDetailDialog
                    open={detailDialogOpen}
                    onClose={() => {
                        setDetailDialogOpen(false);
                        setSelectedEntity(null);
                    }}
                    entity={selectedEntity}
                    entityType="schemabinding"
                    selectedEntityId={selectedEntityId}
                />
            )}
        </Box>
    );
};

export default SchemaBindingManagement;
